import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { isIOS } from 'react-device-detect';
import { toast } from 'react-toastify';
import { useScreenshot } from 'use-react-screenshot';
import { useForecastContext } from '../../contexts/ForecastContextProvider';
import useSavedSpots from '../../hooks/useSavedSpots';
import ForecastMap from '../../components/ForecastMap';
import ForecastSideMenu from '../../components/ForecastSideMenu';
import ForecastRightPanel from '../../components/ForecastRightPanel';
import PointTableV2 from '../../components/PointTableV2';
import SkewTPanel from '../../components/SkewT';
import { b64toBlob } from '../../components/PointTableV2/utils';
import { useTranslation } from 'react-i18next';
import ForecastTopBar from './ForecastTopBar';
import { buildSearchParams, parseUrlState, ROUND_DP } from './urlState';
import { ALL_SITES, FEATURED_TAKE_OFF_SITE } from '../../constants';
import { getStationData } from '../../services/api';
import { useSetDashboardActions } from '../../contexts/DashboardActionsContext';

const roundDp = (n) => {
    const f = Math.pow(10, ROUND_DP);
    return Math.round(n * f) / f;
};

// How many days forward/back the forecast model data covers. The date stepper
// is clamped to [today - N, today + N].
const FORECAST_WINDOW_DAYS = 5;

const ForecastPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setPosition, setDate } = useForecastContext();
    const { spots, addSpot, removeSpot, renameSpot, findSavedByCoords } = useSavedSpots();

    const url = parseUrlState(searchParams);

    // Register share + refresh handlers with the global NavigationHeader.
    // Share copies the current /forecast URL (with lat/lng/date/feature/site).
    // Refresh just reloads the page so PointTableV2 re-fetches.
    const setDashboardActions = useSetDashboardActions();
    useEffect(() => {
        const handleShare = async () => {
            const shareData = { url: window.location.href };
            if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                try { await navigator.share(shareData); } catch (_) {}
                return;
            }
            try { await navigator.clipboard.writeText(window.location.href); } catch (_) {}
        };
        setDashboardActions({
            onRefresh: () => window.location.reload(),
            onShare: handleShare,
            isRefreshing: false,
        });
        return () => setDashboardActions({ onRefresh: null, onShare: null, isRefreshing: false });
    }, [setDashboardActions]);

    // Resolve `site` slug → site metadata (name, id) from the global sites list
    // so the TopBar can show a human-readable label and we can fetch coordinates
    // for site-only URLs.
    const siteInfo = useMemo(() => {
        if (!url.site) return null;
        return ALL_SITES.find((s) => s.slug === url.site)
            || (FEATURED_TAKE_OFF_SITE && FEATURED_TAKE_OFF_SITE.slug === url.site ? FEATURED_TAKE_OFF_SITE : null);
    }, [url.site]);

    // Sync URL → forecast context so PointTableV2 sees the right values.
    useEffect(() => {
        if (url.lat !== null && url.lng !== null) {
            setPosition({ lat: url.lat, lng: url.lng });
        }
        // Date defaults to today when missing.
        const dateToSet = url.date ? dayjs(url.date) : dayjs();
        setDate(dateToSet);
        // We only want to react to URL changes; setters are stable from context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url.lat, url.lng, url.date]);

    const pushUrl = useCallback((patch) => {
        const next = { ...url, ...patch };
        // If lat/lng are missing, feature cannot stay active.
        if (next.lat === null || next.lng === null) next.feature = null;
        navigate({ search: `?${buildSearchParams(next).toString()}` });
    }, [url, navigate]);

    // ---- Side menu ----
    const [sideMenuOpen, setSideMenuOpen] = useState(false); // mobile drawer state

    // ---- Geolocate ----
    const [geolocating, setGeolocating] = useState(false);
    const [geolocationError, setGeolocationError] = useState(null);
    const handleGeolocate = useCallback(() => {
        if (!('geolocation' in navigator)) {
            setGeolocationError(t('Location unavailable'));
            return;
        }
        setGeolocating(true);
        setGeolocationError(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGeolocating(false);
                // Moving to user's geolocation leaves any takeoff-site context.
                pushUrl({ lat: roundDp(pos.coords.latitude), lng: roundDp(pos.coords.longitude), site: null });
            },
            () => {
                setGeolocating(false);
                setGeolocationError(t('Location unavailable'));
                setTimeout(() => setGeolocationError(null), 4000);
            },
            { timeout: 10000, enableHighAccuracy: false },
        );
    }, [pushUrl, t]);

    // When the URL has `site` but no lat/lng (e.g. a shared link
     // /forecast?site=drvar-kruskovac), fetch the site's coordinates and push
     // them into the URL so the rest of the page can render normally. Failures
     // are silent — the user still sees the map and can click a location.
    useEffect(() => {
        if (!siteInfo) return undefined;
        if (url.lat !== null && url.lng !== null) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await getStationData(siteInfo.id);
                if (cancelled || !data) return;
                const lat = parseFloat(data.LAT);
                const lng = parseFloat(data.LONG);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                pushUrl({ lat: roundDp(lat), lng: roundDp(lng) });
            } catch (_) {
                // Silent failure — empty map remains; user can pick manually.
            }
        })();
        return () => { cancelled = true; };
    }, [siteInfo, url.lat, url.lng, pushUrl]);

    // ---- Saved spots ----
    const pin = url.lat !== null && url.lng !== null ? { lat: url.lat, lng: url.lng } : null;
    const savedAs = pin ? findSavedByCoords(pin.lat, pin.lng) : null;
    const handleSaveCurrent = useCallback((name) => {
        if (!pin) return;
        addSpot(name, pin.lat, pin.lng);
    }, [pin, addSpot]);
    const handleLoadSpot = useCallback((spot) => {
        // Loading a saved spot is its own navigation context — drop site slug.
        pushUrl({ lat: spot.lat, lng: spot.lng, site: null });
        setSideMenuOpen(false);
    }, [pushUrl]);

    // ---- Map ----
    const handleLocationPick = useCallback(({ lat, lng }) => {
        // Picking a new spot on the map leaves any takeoff-site context.
        pushUrl({ lat: roundDp(lat), lng: roundDp(lng), site: null });
    }, [pushUrl]);

    // ---- Feature toggle ----
    const canActivateFeature = pin !== null;
    const handleToggleFeature = useCallback((featureKey) => {
        if (!canActivateFeature) return;
        pushUrl({ feature: url.feature === featureKey ? null : featureKey });
        setSideMenuOpen(false);
    }, [pushUrl, url.feature, canActivateFeature]);

    // ---- Date prev/next ----
    // Forecast data is only available for a ±5-day window around today, so the
    // date stepper is clamped to that range (and the arrows disable at the
    // edges via canPrevDate/canNextDate below).
    const currentDate = url.date ? dayjs(url.date) : dayjs();
    const minDate = dayjs().startOf('day').subtract(FORECAST_WINDOW_DAYS, 'day');
    const maxDate = dayjs().startOf('day').add(FORECAST_WINDOW_DAYS, 'day');
    const canPrevDate = currentDate.startOf('day').isAfter(minDate);
    const canNextDate = currentDate.startOf('day').isBefore(maxDate);
    const handlePrevDate = useCallback(() => {
        if (!currentDate.startOf('day').isAfter(minDate)) return;
        pushUrl({ date: currentDate.subtract(1, 'day').format('YYYY-MM-DD') });
    }, [pushUrl, currentDate, minDate]);
    const handleNextDate = useCallback(() => {
        if (!currentDate.startOf('day').isBefore(maxDate)) return;
        pushUrl({ date: currentDate.add(1, 'day').format('YYYY-MM-DD') });
    }, [pushUrl, currentDate, maxDate]);

    // ---- Time slider ----
    const timeChangeTimer = useRef(null);
    const handleTimeChange = useCallback((hhmm) => {
        if (timeChangeTimer.current) clearTimeout(timeChangeTimer.current);
        timeChangeTimer.current = setTimeout(() => {
            pushUrl({ time: hhmm });
            timeChangeTimer.current = null;
        }, 150);
    }, [pushUrl]);

    // ---- Screenshot ----
    // Captures the rendered PointTableV2 inside the right panel and either
    // copies + shares it (desktop / Android) or just notifies the user that
    // the share is ready (iOS — Web Share API needs a fresh user gesture).
    const pointTableRef = useRef(null);
    const pendingShareFile = useRef(null);
    const [, takeScreenshot] = useScreenshot();
    const handleScreenshot = useCallback(async () => {
        if (!pointTableRef.current) return;

        // iOS two-step: first press captures + stores, second press shares
        // using a FRESH user gesture (required by iOS Web Share API).
        if (isIOS && pendingShareFile.current) {
            const file = pendingShareFile.current;
            pendingShareFile.current = null;
            try {
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file] });
                } else {
                    toast.error(t('Screenshot failed'));
                }
            } catch (_) { /* user cancelled */ }
            return;
        }

        try {
            const dataUrl = await takeScreenshot(pointTableRef.current, {
                dpi: 144,
                scale: 2,
                scrollX: 0,
                scrollY: -window.scrollY,
            });
            const blob = await b64toBlob(dataUrl);
            const fileName = `${(siteInfo && siteInfo.name) || 'point'}-${dayjs().format('DDMMYYYY')}.png`;
            const file = new File([blob], fileName, { type: blob.type });

            if (isIOS) {
                pendingShareFile.current = file;
                toast.info(t('Screenshot taken, press same button again to share it'));
                return;
            }
            // Copy to clipboard (best-effort) then offer native share if available.
            try {
                await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                toast.info(t('Screenshot copied to clipboard'), { autoClose: 1200 });
            } catch (_) { /* clipboard blocked — fall through to share */ }
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                try { await navigator.share({ files: [file] }); } catch (_) { /* user cancelled */ }
            }
        } catch (err) {
            toast.error(t('Screenshot failed'));
        }
    }, [takeScreenshot, t, siteInfo]);

    // ---- Close ----
    const handleClose = useCallback(() => {
        navigate(url.site ? `/site/${url.site}` : '/');
    }, [navigate, url.site]);

    const featureKey = url.feature;
    const panelOpen = canActivateFeature && featureKey !== null;

    return (
        <div className="flex flex-col h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] bg-zinc-100 dark:bg-slate-900">
            <ForecastTopBar
                date={currentDate}
                time={url.time || '1200'}
                onTimeChange={panelOpen && featureKey === 'skewt' ? handleTimeChange : null}
                onPrevDate={handlePrevDate}
                onNextDate={handleNextDate}
                canPrevDate={canPrevDate}
                canNextDate={canNextDate}
                siteName={siteInfo ? siteInfo.name : null}
                onClose={handleClose}
                onClosePanel={() => pushUrl({ feature: null })}
                panelOpen={panelOpen}
                onToggleSideMenu={() => setSideMenuOpen((v) => !v)}
            />

            <div className="flex-1 flex flex-row overflow-hidden relative">
                {/* Desktop: always visible; mobile: only when sideMenuOpen.
                    Mobile drawer is `absolute` against the relative parent
                    above, so it pops below the page topbar (not below the
                    global nav). */}
                <div className={`${sideMenuOpen ? 'block absolute z-[1100] inset-y-0 left-0 shadow-xl' : 'hidden'} xl:relative xl:block xl:shadow-none`}>
                    <ForecastSideMenu
                        open
                        spots={spots}
                        onLoadSpot={handleLoadSpot}
                        onRenameSpot={renameSpot}
                        onDeleteSpot={removeSpot}
                        activeFeature={featureKey}
                        canActivateFeature={canActivateFeature}
                        onToggleFeature={handleToggleFeature}
                        pin={pin}
                        onGeolocate={handleGeolocate}
                        geolocating={geolocating}
                        geolocationError={geolocationError}
                        canScreenshot={panelOpen}
                        onScreenshot={handleScreenshot}
                        canSave={pin !== null}
                        savedAs={savedAs}
                        onSaveCurrent={handleSaveCurrent}
                        onRenameSaved={renameSpot}
                        onRemoveSaved={removeSpot}
                    />
                </div>

                {/* Map / panel widths depend on the active feature on desktop:
                      - skewt: panel = 60vw (Skysight uses 63vw but our chart has
                        ~77px less height due to extra chrome row; 60vw keeps
                        Skysight-equivalent plot aspect ratio across viewport sizes).
                        Map absorbs the leftover.
                      - point: at xl (incl. a default 1512px 14"), panel takes
                        flex-1 (tables benefit from width) and the map stays at a
                        fixed 320px. But on truly large screens (2xl, ≥1536px) a
                        320px map looks like a sliver next to a huge table, while
                        the skewt map grows — so at 2xl the point view MIRRORS the
                        skewt layout (map flex-1, panel 60vw) so the map/table
                        ratio matches the map/skewt ratio. The table still gets
                        60vw (plenty wide; it scrolls horizontally anyway).
                    On mobile (<xl) the map hides entirely when a panel is open. */}
                <div className={`min-w-0 ${panelOpen
                    ? (featureKey === 'skewt'
                        ? 'hidden xl:block xl:flex-1'
                        : 'hidden xl:block xl:w-[320px] xl:flex-shrink-0 2xl:w-auto 2xl:flex-1')
                    : 'flex-1 block'}`}>
                    <ForecastMap pin={pin} onLocationPick={handleLocationPick} />
                </div>

                <ForecastRightPanel
                    open={panelOpen}
                    widthClass={featureKey === 'skewt' ? 'flex-1 xl:w-[60vw] xl:flex-none' : 'flex-1 2xl:w-[60vw] 2xl:flex-none'}
                >
                    {featureKey === 'point' && <PointTableV2 ref={pointTableRef} />}
                    {featureKey === 'skewt' && <SkewTPanel ref={pointTableRef} />}
                </ForecastRightPanel>
            </div>
        </div>
    );
};

export default ForecastPage;
